import { useMemo, useState } from 'react';
import {
    BlockStack,
    Box,
    Button,
    Card,
    IconButton,
    InlineStack,
    SegmentedControl,
    TextField,
    Typography,
    type SegmentedOption,
} from '@pv-frontend/ui-components-web';

/** A weighted arm being drafted in the form; weights are held as raw input text. */
interface VariantDraft {
    name: string;
    weight: string;
}

export interface DefineExperimentValues {
    key: string;
    flagKey: string;
    variants: readonly { name: string; weight: number }[];
}

export interface DefineExperimentFormProps {
    /** Flag keys the experiment may decorate (from the flag catalogue). */
    flagKeys: readonly string[];
    /** Keys of experiments that already exist, so the form can reject a duplicate. */
    existingKeys?: readonly string[];
    /** True while a define mutation is in flight — disables the form. */
    busy?: boolean;
    onDefine: (values: DefineExperimentValues) => void;
}

const DEFAULT_VARIANTS: readonly VariantDraft[] = [
    { name: 'control', weight: '50' },
    { name: 'treatment', weight: '50' },
];

/** An experiment / variant key: lowercase alphanumerics, dashes and underscores. */
const KEY_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

/** A positive whole-number weight. */
function weightValue(raw: string): number | null {
    const trimmed = raw.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const value = Number(trimmed);
    return value >= 1 ? value : null;
}

interface FormErrors {
    key?: string;
    variants?: string;
}

/** Validate the whole draft; an empty object means it's ready to submit. */
function validate(
    key: string,
    variants: readonly VariantDraft[],
    existingKeys: readonly string[],
): FormErrors {
    const errors: FormErrors = {};
    const trimmedKey = key.trim();
    if (!trimmedKey) errors.key = 'Enter an experiment key.';
    else if (!KEY_PATTERN.test(trimmedKey)) errors.key = 'Use lowercase letters, numbers, - or _.';
    else if (existingKeys.includes(trimmedKey)) errors.key = 'An experiment with this key already exists.';

    const names = variants.map((v) => v.name.trim());
    if (variants.length < 2) {
        errors.variants = 'Add at least two variants.';
    } else if (names.some((name) => !name)) {
        errors.variants = 'Every variant needs a name.';
    } else if (new Set(names).size !== names.length) {
        errors.variants = 'Variant names must be unique.';
    } else if (variants.some((v) => weightValue(v.weight) === null)) {
        errors.variants = 'Every weight must be a whole number ≥ 1.';
    }
    return errors;
}

/** Percentage split for a weight against the running total (0 when the total is 0). */
function splitPercent(weight: string, total: number): number {
    const value = weightValue(weight);
    return value === null || total === 0 ? 0 : Math.round((value / total) * 100);
}

/**
 * Define an A/B experiment: a unique key, the flag it decorates, and two or more
 * weighted arms. Weights are relative integers; the form shows the resulting
 * percentage split so the operator can sanity-check it. Purely presentational —
 * the screen wires `onDefine` to the `defineExperiment` mutation.
 */
export function DefineExperimentForm({
    flagKeys,
    existingKeys = [],
    busy = false,
    onDefine,
}: DefineExperimentFormProps) {
    const [key, setKey] = useState('');
    const [flagKey, setFlagKey] = useState(flagKeys[0] ?? '');
    const [variants, setVariants] = useState<VariantDraft[]>(() => DEFAULT_VARIANTS.map((v) => ({ ...v })));
    const [submitted, setSubmitted] = useState(false);

    const errors = validate(key, variants, existingKeys);
    const valid = Object.keys(errors).length === 0 && flagKey !== '';
    const totalWeight = variants.reduce((sum, v) => sum + (weightValue(v.weight) ?? 0), 0);

    const flagOptions = useMemo<SegmentedOption<string>[]>(
        () => flagKeys.map((flag) => ({ value: flag, label: flag })),
        [flagKeys],
    );

    const setVariant = (index: number, patch: Partial<VariantDraft>) =>
        setVariants((current) => current.map((v, i) => (i === index ? { ...v, ...patch } : v)));

    const addVariant = () => setVariants((current) => [...current, { name: '', weight: '50' }]);

    const removeVariant = (index: number) =>
        setVariants((current) => current.filter((_, i) => i !== index));

    const submit = () => {
        setSubmitted(true);
        if (!valid) return;
        onDefine({
            key: key.trim(),
            flagKey,
            variants: variants.map((v) => ({ name: v.name.trim(), weight: weightValue(v.weight) as number })),
        });
    };

    const showError = (message?: string) => (submitted ? message : undefined);

    return (
        <Card>
            <Box padding="400">
                <BlockStack gap="400">
                    <Typography variant="heading-sm" as="h2">
                        Define an experiment
                    </Typography>

                    <TextField
                        label="Experiment key"
                        value={key}
                        disabled={busy}
                        error={showError(errors.key)}
                        placeholder="e.g. voice-onboarding"
                        onChange={setKey}
                    />

                    <BlockStack gap="150">
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Flag to decorate
                        </Typography>
                        {flagOptions.length > 0 ? (
                            <SegmentedControl
                                accessibilityLabel="Flag to decorate"
                                options={flagOptions}
                                value={flagKey}
                                disabled={busy}
                                onChange={setFlagKey}
                            />
                        ) : (
                            <Typography variant="body-sm" tone="text-secondary">
                                No flags are available to decorate.
                            </Typography>
                        )}
                    </BlockStack>

                    <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center" gap="300">
                            <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                                Variants
                            </Typography>
                            <Typography variant="body-sm" tone="text-secondary">
                                Total weight {totalWeight}
                            </Typography>
                        </InlineStack>

                        {variants.map((variant, index) => (
                            <InlineStack key={index} gap="200" blockAlign="end" wrap={false}>
                                <Box flex={1} minWidth={0}>
                                    <TextField
                                        label={`Variant ${index + 1} name`}
                                        value={variant.name}
                                        disabled={busy}
                                        placeholder="control"
                                        onChange={(name) => setVariant(index, { name })}
                                    />
                                </Box>
                                <Box minWidth={110}>
                                    <TextField
                                        label="Weight"
                                        type="number"
                                        inputMode="numeric"
                                        min={1}
                                        value={variant.weight}
                                        disabled={busy}
                                        onChange={(weight) => setVariant(index, { weight })}
                                    />
                                </Box>
                                <Box minWidth={48}>
                                    <Typography variant="body-sm" tone="text-secondary">
                                        {splitPercent(variant.weight, totalWeight)}%
                                    </Typography>
                                </Box>
                                <IconButton
                                    icon="x"
                                    accessibilityLabel={`Remove variant ${index + 1}`}
                                    disabled={busy || variants.length <= 2}
                                    onPress={() => removeVariant(index)}
                                />
                            </InlineStack>
                        ))}

                        {submitted && errors.variants ? (
                            <Typography variant="body-sm" tone="text-critical">
                                {errors.variants}
                            </Typography>
                        ) : null}

                        <Box>
                            <Button size="slim" variant="tertiary" disabled={busy} onPress={addVariant}>
                                Add variant
                            </Button>
                        </Box>
                    </BlockStack>

                    <Box>
                        <Button
                            variant="primary"
                            size="slim"
                            disabled={busy || (submitted && !valid)}
                            isLoading={busy}
                            onPress={submit}
                        >
                            Define experiment
                        </Button>
                    </Box>
                </BlockStack>
            </Box>
        </Card>
    );
}
